import React from 'react';

/**
 * Section 6 : Évaluation de l'autonomie (AVQ)
 * Légende: A = Autonome, D = Avec difficulté, AP = Aide partielle, AT = Aide totale, N = Ne fait plus
 */
const AssessmentSection6_Autonomie = ({ data, onChange }) => {
  const handleTableChange = (category, activity, value) => {
    onChange('autonomy', {
      ...data,
      [category]: {
        ...data?.[category],
        [activity]: value
      }
    });
  };

  const autonomyOptions = [
    { value: 'A', label: 'A (Autonome)' },
    { value: 'D', label: 'D (Avec difficulté)' },
    { value: 'AP', label: 'AP (Aide partielle)' },
    { value: 'AT', label: 'AT (Aide totale)' },
    { value: 'N', label: 'N (Ne fait plus)' }
  ];

  const renderActivityRow = (category, activity, label) => {
    console.log('Rendering activity row:', { category, activity, label });
    return (
      <tr key={activity}>
        <td className="activity-label-cell">
          <span className="activity-label-text">{label}</span>
        </td>
        {autonomyOptions.map(option => (
          <td key={option.value} style={{ textAlign: 'center', padding: '12px' }}>
            <input
              type="radio"
              name={`${category}-${activity}`}
              checked={data?.[category]?.[activity] === option.value}
              onChange={() => handleTableChange(category, activity, option.value)}
            />
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className="assessment-section-content">
      <h3 className="assessment-section-title">ÉVALUATION DE L'AUTONOMIE</h3>

      <div className="assessment-info-card">
        <p><strong>Légende :</strong></p>
        <p><strong>A</strong> : Autonome (je fais seul(e) sans difficulté)</p>
        <p><strong>D</strong> : Avec difficulté (je fais seul(e) mais c'est difficile)</p>
        <p><strong>AP</strong> : Aide partielle nécessaire</p>
        <p><strong>AT</strong> : Aide totale nécessaire</p>
        <p><strong>N</strong> : Ne fait plus / Impossible</p>
      </div>

      {/* Hygiène et soins personnels */}
      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '24px', marginBottom: '16px' }}>
        Hygiène et soins personnels
      </h4>

      <table className="assessment-table">
        <thead>
          <tr>
            <th style={{ minWidth: '200px', width: 'auto' }}>Activité</th>
            <th style={{ textAlign: 'center', width: '80px' }}>A</th>
            <th style={{ textAlign: 'center', width: '80px' }}>D</th>
            <th style={{ textAlign: 'center', width: '80px' }}>AP</th>
            <th style={{ textAlign: 'center', width: '80px' }}>AT</th>
            <th style={{ textAlign: 'center', width: '80px' }}>N</th>
          </tr>
        </thead>
        <tbody>
          {renderActivityRow('hygiene', 'completeWash', 'Toilette complète')}
          {renderActivityRow('hygiene', 'showerBath', 'Douche / Bain')}
          {renderActivityRow('hygiene', 'dressingUpper', 'S\'habiller (haut du corps)')}
          {renderActivityRow('hygiene', 'dressingLower', 'S\'habiller (bas du corps)')}
          {renderActivityRow('hygiene', 'shoes', 'Mettre ses chaussures')}
        </tbody>
      </table>

      {/* Alimentation */}
      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '32px', marginBottom: '16px' }}>
        Alimentation
      </h4>

      <table className="assessment-table">
        <thead>
          <tr>
            <th style={{ minWidth: '200px', width: 'auto' }}>Activité</th>
            <th style={{ textAlign: 'center', width: '80px' }}>A</th>
            <th style={{ textAlign: 'center', width: '80px' }}>D</th>
            <th style={{ textAlign: 'center', width: '80px' }}>AP</th>
            <th style={{ textAlign: 'center', width: '80px' }}>AT</th>
            <th style={{ textAlign: 'center', width: '80px' }}>N</th>
          </tr>
        </thead>
        <tbody>
          {renderActivityRow('nutrition', 'prepareMeal', 'Préparer un repas')}
          {renderActivityRow('nutrition', 'reheatMeal', 'Réchauffer un plat')}
          {renderActivityRow('nutrition', 'eatAlone', 'Manger seul(e)')}
          {renderActivityRow('nutrition', 'cutFood', 'Couper les aliments')}
          {renderActivityRow('nutrition', 'drinkAlone', 'Boire seul(e)')}
        </tbody>
      </table>

      <div className="assessment-grid-2" style={{ marginTop: '20px' }}>
        <div className="assessment-form-group">
          <label>Appétit actuel</label>
          <select
            className="assessment-form-control"
            value={data?.nutrition?.appetite || ''}
            onChange={(e) => handleTableChange('nutrition', 'appetite', e.target.value)}
          >
            <option value="">-- Sélectionner --</option>
            <option value="bon">Bon</option>
            <option value="moyen">Moyen</option>
            <option value="faible">Faible / Perte d'appétit</option>
          </select>
        </div>

        <div className="assessment-form-group">
          <label>Poids (kg)</label>
          <input
            type="number"
            className="assessment-form-control"
            value={data?.nutrition?.weight || ''}
            onChange={(e) => handleTableChange('nutrition', 'weight', parseFloat(e.target.value) || null)}
          />
        </div>
      </div>

      {/* Mobilité et transferts */}
      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '32px', marginBottom: '16px' }}>
        Mobilité et transferts
      </h4>

      <table className="assessment-table">
        <thead>
          <tr>
            <th style={{ minWidth: '200px', width: 'auto' }}>Activité</th>
            <th style={{ textAlign: 'center', width: '80px' }}>A</th>
            <th style={{ textAlign: 'center', width: '80px' }}>D</th>
            <th style={{ textAlign: 'center', width: '80px' }}>AP</th>
            <th style={{ textAlign: 'center', width: '80px' }}>AT</th>
            <th style={{ textAlign: 'center', width: '80px' }}>N</th>
          </tr>
        </thead>
        <tbody>
          {renderActivityRow('mobility', 'getUpFromBed', 'Se lever du lit')}
          {renderActivityRow('mobility', 'lieDown', 'Se coucher')}
          {renderActivityRow('mobility', 'getUpFromChair', 'Se lever d\'un fauteuil')}
          {renderActivityRow('mobility', 'stairs', 'Monter / descendre escaliers')}
          {renderActivityRow('mobility', 'moveIndoor', 'Se déplacer dans le logement')}
          {renderActivityRow('mobility', 'leaveHome', 'Sortir du logement')}
        </tbody>
      </table>

      {/* Entretien du logement */}
      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '32px', marginBottom: '16px' }}>
        Entretien du logement
      </h4>

      <table className="assessment-table">
        <thead>
          <tr>
            <th style={{ minWidth: '200px', width: 'auto' }}>Activité</th>
            <th style={{ textAlign: 'center', width: '80px' }}>A</th>
            <th style={{ textAlign: 'center', width: '80px' }}>D</th>
            <th style={{ textAlign: 'center', width: '80px' }}>AP</th>
            <th style={{ textAlign: 'center', width: '80px' }}>AT</th>
            <th style={{ textAlign: 'center', width: '80px' }}>N</th>
          </tr>
        </thead>
        <tbody>
          {renderActivityRow('housekeeping', 'dailyTidying', 'Rangement quotidien')}
          {renderActivityRow('housekeeping', 'vacuuming', 'Passer l\'aspirateur')}
          {renderActivityRow('housekeeping', 'washFloors', 'Laver les sols')}
          {renderActivityRow('housekeeping', 'dishes', 'Faire la vaisselle')}
          {renderActivityRow('housekeeping', 'laundry', 'Lessive')}
        </tbody>
      </table>

      {/* Activités domestiques */}
      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '32px', marginBottom: '16px' }}>
        Activités domestiques
      </h4>

      <table className="assessment-table">
        <thead>
          <tr>
            <th style={{ minWidth: '200px', width: 'auto' }}>Activité</th>
            <th style={{ textAlign: 'center', width: '80px' }}>A</th>
            <th style={{ textAlign: 'center', width: '80px' }}>D</th>
            <th style={{ textAlign: 'center', width: '80px' }}>AP</th>
            <th style={{ textAlign: 'center', width: '80px' }}>AT</th>
            <th style={{ textAlign: 'center', width: '80px' }}>N</th>
          </tr>
        </thead>
        <tbody>
          {renderActivityRow('domestic', 'shopping', 'Faire les courses')}
          {renderActivityRow('domestic', 'carryBags', 'Porter les sacs')}
          {renderActivityRow('domestic', 'manageMail', 'Gérer le courrier')}
          {renderActivityRow('domestic', 'usePhone', 'Utiliser le téléphone')}
          {renderActivityRow('domestic', 'manageBudget', 'Gérer son budget')}
        </tbody>
      </table>

      {/* Fonctions cognitives */}
      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '32px', marginBottom: '16px' }}>
        Fonctions cognitives et communication
      </h4>

      <div className="assessment-grid-2">
        <div className="assessment-form-group">
          <label>Mémoire</label>
          <select
            className="assessment-form-control"
            value={data?.cognitive?.memory || ''}
            onChange={(e) => handleTableChange('cognitive', 'memory', e.target.value)}
          >
            <option value="">-- Sélectionner --</option>
            <option value="aucun-trouble">Aucun trouble</option>
            <option value="legers-oublis">Légers oublis</option>
            <option value="troubles-moderes">Troubles modérés</option>
            <option value="troubles-severes">Troubles sévères</option>
          </select>
        </div>

        <div className="assessment-form-group">
          <label>Orientation temporelle</label>
          <select
            className="assessment-form-control"
            value={data?.cognitive?.temporalOrientation || ''}
            onChange={(e) => handleTableChange('cognitive', 'temporalOrientation', e.target.value)}
          >
            <option value="">-- Sélectionner --</option>
            <option value="oriente">Orienté(e)</option>
            <option value="partiellement">Partiellement désorienté(e)</option>
            <option value="totalement">Totalement désorienté(e)</option>
          </select>
        </div>

        <div className="assessment-form-group">
          <label>Vision</label>
          <select
            className="assessment-form-control"
            value={data?.cognitive?.vision || ''}
            onChange={(e) => handleTableChange('cognitive', 'vision', e.target.value)}
          >
            <option value="">-- Sélectionner --</option>
            <option value="bonne">Bonne</option>
            <option value="moyenne">Moyenne (corrigée)</option>
            <option value="mauvaise">Mauvaise</option>
            <option value="cecite">Cécité partielle/totale</option>
          </select>
        </div>

        <div className="assessment-form-group">
          <label>Audition</label>
          <select
            className="assessment-form-control"
            value={data?.cognitive?.hearing || ''}
            onChange={(e) => handleTableChange('cognitive', 'hearing', e.target.value)}
          >
            <option value="">-- Sélectionner --</option>
            <option value="bonne">Bonne</option>
            <option value="moyenne">Moyenne (appareillée)</option>
            <option value="mauvaise">Mauvaise</option>
            <option value="surdite">Surdité importante</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default AssessmentSection6_Autonomie;
